import { LAYOUT_COMPONENT_TYPE, REGISTRY_BLOCK, type LayoutBlock } from '@web-studio/shared-schemas';
import { loc3 } from '../locales';
import { FIXTURE_BLOCK_IDS } from '../uuids';

/**
 * Demo menu inspired by DE sushi-bar Speisekarten (Berlin / München sectioning:
 * Nigiri, Maki, Sashimi, Omakase/Sets, Sake) — original fictional venue “Kiefer Theke”.
 */
const SUSHI_MENU_JSON = {
  de: JSON.stringify([
    {
      title: 'Nigiri (2 Stk.)',
      items: [
        { name: 'Sake', price: '7,50', description: 'Lachs, handgeformter Reis' },
        { name: 'Maguro Akami', price: '8,50', description: 'Thunfisch, kernig' },
        { name: 'Hamachi', price: '8,00', description: 'Gelbschwanz' },
        { name: 'Hotate Aburi', price: '9,50', description: 'Jakobsmuschel, leicht angeflammt' },
        { name: 'Unagi', price: '8,00', description: 'Aal, süße Glasur' },
        { name: 'Tamago', price: '5,50', description: 'Japanischer Eierstich' },
      ],
    },
    {
      title: 'Makimono (6 Stk.)',
      items: [
        { name: 'Kappa Maki', price: '5,00', description: 'Gurke, Nori' },
        { name: 'Sake Maki', price: '6,50', description: 'Lachs' },
        { name: 'Tekka Maki', price: '7,00', description: 'Thunfisch' },
        { name: 'Sake Avocado', price: '7,50', description: 'Lachs, Avocado' },
        { name: 'Spicy Salmon', price: '8,00', description: 'Lachs, Chili-Mayo' },
      ],
    },
    {
      title: 'Sashimi',
      items: [
        { name: 'Sake Sashimi', price: '14', description: '6 Scheiben Lachs' },
        { name: 'Maguro Sashimi', price: '16', description: '6 Scheiben Thunfisch' },
        { name: 'Moriawase', price: '28', description: 'Tagesauswahl, 12 Scheiben' },
      ],
    },
    {
      title: 'Omakase & Sets',
      items: [
        { name: 'Theken-Omakase', price: '54', description: '8 Nigiri + Handroll des Tages' },
        { name: 'Omakase Eleven', price: '72', description: '11 Nigiri, Chef’s Auswahl' },
        { name: 'Mittag Moriawase', price: '22', description: 'Nigiri & Maki, Di–Fr bis 15 Uhr' },
      ],
    },
    {
      title: 'Alkoholfreie Getränke',
      items: [
        { name: 'Hausgrüntee', price: '3,50', description: 'Warm oder kalt, 0,3 l' },
        { name: 'Ingwer-Limonade', price: '4,20', description: 'Hausgemacht, 0,4 l' },
        { name: 'Mineralwasser still', price: '3,20', description: '0,75 l' },
      ],
    },
    {
      title: 'Bier & Sake',
      items: [
        { name: 'Sapporo Draft', price: '4,80', description: 'Japanisches Lager, 0,33 l' },
        { name: 'Junmai Sake (Glas)', price: '7,00', description: 'Trocken, leicht, 0,1 l' },
        { name: 'Ginjo Sake (Glas)', price: '9,00', description: 'Fruchtig, 0,1 l' },
        { name: 'Umeshu', price: '6,50', description: 'Pflaumenwein, 0,1 l' },
      ],
    },
  ]),
  en: JSON.stringify([
    {
      title: 'Nigiri (2 pcs)',
      items: [
        { name: 'Sake', price: '7.50', description: 'Salmon on hand-pressed rice' },
        { name: 'Maguro Akami', price: '8.50', description: 'Lean tuna' },
        { name: 'Hamachi', price: '8.00', description: 'Yellowtail' },
        { name: 'Hotate Aburi', price: '9.50', description: 'Seared scallop' },
        { name: 'Unagi', price: '8.00', description: 'Eel, sweet glaze' },
        { name: 'Tamago', price: '5.50', description: 'Japanese omelette' },
      ],
    },
    {
      title: 'Makimono (6 pcs)',
      items: [
        { name: 'Kappa Maki', price: '5.00', description: 'Cucumber, nori' },
        { name: 'Sake Maki', price: '6.50', description: 'Salmon' },
        { name: 'Tekka Maki', price: '7.00', description: 'Tuna' },
        { name: 'Sake Avocado', price: '7.50', description: 'Salmon, avocado' },
        { name: 'Spicy Salmon', price: '8.00', description: 'Salmon, chili mayo' },
      ],
    },
    {
      title: 'Sashimi',
      items: [
        { name: 'Sake Sashimi', price: '14', description: '6 slices salmon' },
        { name: 'Maguro Sashimi', price: '16', description: '6 slices tuna' },
        { name: 'Moriawase', price: '28', description: 'Chef’s selection, 12 slices' },
      ],
    },
    {
      title: 'Omakase & sets',
      items: [
        { name: 'Counter omakase', price: '54', description: '8 nigiri + daily handroll' },
        { name: 'Omakase Eleven', price: '72', description: '11 nigiri, chef’s pick' },
        { name: 'Lunch moriawase', price: '22', description: 'Nigiri & maki, Tue–Fri until 15:00' },
      ],
    },
    {
      title: 'Soft drinks',
      items: [
        { name: 'House green tea', price: '3.50', description: 'Hot or iced, 0.3 l' },
        { name: 'Ginger lemonade', price: '4.20', description: 'House-made, 0.4 l' },
        { name: 'Still mineral water', price: '3.20', description: '0.75 l' },
      ],
    },
    {
      title: 'Beer & sake',
      items: [
        { name: 'Sapporo Draft', price: '4.80', description: 'Japanese lager, 0.33 l' },
        { name: 'Junmai sake (glass)', price: '7.00', description: 'Dry, light, 0.1 l' },
        { name: 'Ginjo sake (glass)', price: '9.00', description: 'Fruity, 0.1 l' },
        { name: 'Umeshu', price: '6.50', description: 'Plum wine, 0.1 l' },
      ],
    },
  ]),
  vi: JSON.stringify([
    {
      title: 'Nigiri (2 miếng)',
      items: [
        { name: 'Sake', price: '7,50', description: 'Cá hồi trên cơm ép tay' },
        { name: 'Maguro Akami', price: '8,50', description: 'Cá ngừ nạc' },
        { name: 'Hamachi', price: '8,00', description: 'Cá cam' },
        { name: 'Hotate Aburi', price: '9,50', description: 'Sò điệp áp lửa nhẹ' },
        { name: 'Unagi', price: '8,00', description: 'Lươn, sốt ngọt' },
        { name: 'Tamago', price: '5,50', description: 'Trứng cuộn Nhật' },
      ],
    },
    {
      title: 'Makimono (6 miếng)',
      items: [
        { name: 'Kappa Maki', price: '5,00', description: 'Dưa chuột, nori' },
        { name: 'Sake Maki', price: '6,50', description: 'Cá hồi' },
        { name: 'Tekka Maki', price: '7,00', description: 'Cá ngừ' },
        { name: 'Sake Avocado', price: '7,50', description: 'Cá hồi, bơ' },
        { name: 'Spicy Salmon', price: '8,00', description: 'Cá hồi, mayo ớt' },
      ],
    },
    {
      title: 'Sashimi',
      items: [
        { name: 'Sake Sashimi', price: '14', description: '6 lát cá hồi' },
        { name: 'Maguro Sashimi', price: '16', description: '6 lát cá ngừ' },
        { name: 'Moriawase', price: '28', description: 'Lựa chọn trong ngày, 12 lát' },
      ],
    },
    {
      title: 'Omakase & set',
      items: [
        { name: 'Omakase quầy', price: '54', description: '8 nigiri + handroll trong ngày' },
        { name: 'Omakase Eleven', price: '72', description: '11 nigiri do bếp chọn' },
        { name: 'Moriawase trưa', price: '22', description: 'Nigiri & maki, T3–T6 đến 15:00' },
      ],
    },
    {
      title: 'Đồ uống không cồn',
      items: [
        { name: 'Trà xanh nhà', price: '3,50', description: 'Nóng hoặc lạnh, 0,3 l' },
        { name: 'Limonade gừng', price: '4,20', description: 'Tự làm, 0,4 l' },
        { name: 'Nước khoáng', price: '3,20', description: '0,75 l' },
      ],
    },
    {
      title: 'Bia & sake',
      items: [
        { name: 'Sapporo Draft', price: '4,80', description: 'Lager Nhật, 0,33 l' },
        { name: 'Junmai sake (ly)', price: '7,00', description: 'Khô, nhẹ, 0,1 l' },
        { name: 'Ginjo sake (ly)', price: '9,00', description: 'Trái cây, 0,1 l' },
        { name: 'Umeshu', price: '6,50', description: 'Rượu mơ, 0,1 l' },
      ],
    },
  ]),
};

const SUSHI_HIGHLIGHTS_JSON = {
  de: JSON.stringify([
    {
      title: 'Heute an der Theke',
      items: [
        {
          name: 'Theken-Omakase',
          price: '54',
          description: '8 Nigiri + Handroll des Tages — unser Favorit',
        },
        {
          name: 'Sake Aburi Nigiri',
          price: '8,50',
          description: 'Lachs leicht angeflammt, 2 Stk.',
        },
        {
          name: 'Spicy Salmon Maki',
          price: '8,00',
          description: 'Lachs, Chili-Mayo, 6 Stk.',
        },
        {
          name: 'Sashimi Moriawase',
          price: '28',
          description: 'Tagesauswahl, 12 Scheiben',
        },
      ],
    },
  ]),
  en: JSON.stringify([
    {
      title: 'Today at the counter',
      items: [
        {
          name: 'Counter omakase',
          price: '54',
          description: '8 nigiri + daily handroll — our favorite',
        },
        {
          name: 'Sake aburi nigiri',
          price: '8.50',
          description: 'Lightly seared salmon, 2 pcs',
        },
        {
          name: 'Spicy salmon maki',
          price: '8.00',
          description: 'Salmon, chili mayo, 6 pcs',
        },
        {
          name: 'Sashimi moriawase',
          price: '28',
          description: 'Chef’s selection, 12 slices',
        },
      ],
    },
  ]),
  vi: JSON.stringify([
    {
      title: 'Hôm nay tại quầy',
      items: [
        {
          name: 'Omakase quầy',
          price: '54',
          description: '8 nigiri + handroll trong ngày — món yêu thích',
        },
        {
          name: 'Sake aburi nigiri',
          price: '8,50',
          description: 'Cá hồi áp lửa nhẹ, 2 miếng',
        },
        {
          name: 'Spicy salmon maki',
          price: '8,00',
          description: 'Cá hồi, mayo ớt, 6 miếng',
        },
        {
          name: 'Sashimi moriawase',
          price: '28',
          description: 'Lựa chọn trong ngày, 12 lát',
        },
      ],
    },
  ]),
};

const SUSHI_IMAGE =
  'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=1400&q=80';

export const sushiNavigationHeader: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiNavigation,
  componentType: LAYOUT_COMPONENT_TYPE.navigationHeader,
  displayOrder: 0,
  content: {
    siteTitle: loc3('Kiefer Theke', 'Kiefer Theke', 'Kiefer Theke'),
    navLink1: loc3('Speisekarte', 'Menu', 'Thực đơn'),
    navLink2: loc3('Omakase', 'Omakase', 'Omakase'),
    navLink3: loc3('Kontakt', 'Contact', 'Liên hệ'),
    ctaLabel: loc3('Platz an der Theke', 'Counter seat', 'Chỗ quầy'),
  },
  media: {
    logoImage: '/platform/defaults/logos/nav-mark-restaurant-on-dark.svg',
  },
};

export const sushiHeroEditorial: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiHero,
  componentType: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  displayOrder: 1,
  content: {
    eyebrow: loc3('Sushi-Bar · München', 'Sushi bar · Munich', 'Quầy sushi · München'),
    heading: loc3(
      'Frischer Fisch, ruhige Theke',
      'Fresh fish, a quiet counter',
      'Cá tươi, quầy yên tĩnh',
    ),
    subheading: loc3(
      'Nigiri, Maki und Omakase — tagesfrisch, kompakt, ohne Spektakel.',
      'Nigiri, maki, and omakase — day-fresh, compact, without spectacle.',
      'Nigiri, maki và omakase — tươi trong ngày, gọn, không phô trương.',
    ),
    ctaPrimary: loc3('Theke reservieren', 'Reserve the counter', 'Đặt chỗ quầy'),
    ctaSecondary: loc3('Speisekarte', 'View menu', 'Xem thực đơn'),
  },
  media: {
    heroImage: SUSHI_IMAGE,
  },
};

/** Homepage promoted / favorite set (not the full Speisekarte). */
export const sushiMenuGrid: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiMenu,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  displayOrder: 2,
  content: {
    sectionTitle: loc3('Empfehlungen', 'Favorites', 'Gợi ý'),
    sectionSubtitle: loc3(
      'Eine kleine Auswahl von der Theke — volle Karte inkl. Getränke unter Speisekarte.',
      'A short counter selection — full menu including drinks on the Menu page.',
      'Vài món tại quầy — thực đơn đầy đủ kèm đồ uống ở trang Speisekarte.',
    ),
    menuData: SUSHI_HIGHLIGHTS_JSON,
  },
  media: {
    menuImage: SUSHI_IMAGE,
  },
};

/** Full Speisekarte (+ drinks) — used on the Menu page in studio bootstrap. */
export const sushiDynamicMenu: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiDynamicMenu,
  componentType: LAYOUT_COMPONENT_TYPE.dynamicMenuFlow,
  registryBlockKey: REGISTRY_BLOCK.presentation.dynamicMenuFlow,
  displayOrder: 3,
  content: {
    sectionTitle: loc3('Speisekarte', 'Full menu', 'Thực đơn đầy đủ'),
    sectionSubtitle: loc3(
      'Nach Kategorie — inkl. Getränke, Bier und Sake.',
      'By category — including drinks, beer, and sake.',
      'Theo danh mục — gồm đồ uống, bia và sake.',
    ),
    menuData: SUSHI_MENU_JSON,
  },
  media: {
    menuImage: SUSHI_IMAGE,
  },
};

export const sushiChefStory: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiChef,
  componentType: LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  registryBlockKey: REGISTRY_BLOCK.presentation.chefStoryBlock,
  displayOrder: 4,
  content: {
    sectionTitle: loc3('An der Theke', 'At the counter', 'Tại quầy'),
    chefName: loc3('Itamae Kenji Mori', 'Itamae Kenji Mori', 'Itamae Kenji Mori'),
    chefTitle: loc3('Sushi-Meister', 'Sushi chef', 'Bếp trưởng sushi'),
    body: loc3(
      'Reis mit Geduld, Fisch nach Tagesfang — die Karte bleibt kurz, die Theke im Blick.',
      'Patient rice, fish by the day’s catch — a short menu, eyes on the counter.',
      'Cơm kiên nhẫn, cá theo ngày — thực đơn ngắn, mắt hướng về quầy.',
    ),
  },
  media: {
    portraitImage:
      'https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?auto=format&fit=crop&w=960&q=80',
  },
};

export const sushiReservationForm: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiReservation,
  componentType: LAYOUT_COMPONENT_TYPE.reservationFormEmbed,
  registryBlockKey: REGISTRY_BLOCK.presentation.reservationFormEmbed,
  displayOrder: 4,
  content: {
    heading: loc3('Platz an der Theke', 'Counter reservation', 'Đặt chỗ quầy'),
    supporting: loc3(
      'Begrenzte Thekenplätze — bitte Wunschzeit und Personenanzahl angeben.',
      'Limited counter seats — share preferred time and party size.',
      'Chỗ quầy có hạn — gửi giờ mong muốn và số khách.',
    ),
    ctaLabel: loc3('Anfrage senden', 'Send request', 'Gửi yêu cầu'),
    note: loc3(
      'Omakase Eleven bitte mindestens 24 Stunden vorher anfragen.',
      'Please request Omakase Eleven at least 24 hours ahead.',
      'Omakase Eleven vui lòng hỏi trước ít nhất 24 giờ.',
    ),
  },
  media: {},
};

export const sushiTastingTimeline: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiTasting,
  componentType: LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline,
  registryBlockKey: REGISTRY_BLOCK.presentation.tastingMenuCourseTimeline,
  displayOrder: 6,
  content: {
    heading: loc3('Omakase am Abend', 'Evening omakase', 'Omakase buổi tối'),
    supporting: loc3(
      'Chef’s Auswahl — tagesfrisch, an der Theke serviert.',
      'Chef’s selection — day-fresh, served at the counter.',
      'Lựa chọn của bếp — tươi trong ngày, phục vụ tại quầy.',
    ),
    courses: loc3(
      'Otoshi\nSashimi des Tages\nNigiri I–IV\nAburi Special\nHandroll\nTamago & Tee',
      'Otoshi\nDaily sashimi\nNigiri I–IV\nAburi special\nHandroll\nTamago & tea',
      'Otoshi\nSashimi trong ngày\nNigiri I–IV\nAburi đặc biệt\nHandroll\nTamago & trà',
    ),
  },
  media: {},
};

export const sushiAllergyForm: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiAllergy,
  componentType: LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm,
  registryBlockKey: REGISTRY_BLOCK.presentation.allergyCaptureSecureForm,
  displayOrder: 7,
  content: {
    heading: loc3(
      'Allergien & Rohfisch-Hinweise',
      'Allergies & raw-fish notes',
      'Dị ứng & lưu ý cá sống',
    ),
    supporting: loc3(
      'Bitte Unverträglichkeiten (z. B. Fisch, Schalenfrüchte, Soja, Gluten) vor dem Besuch mitteilen.',
      'Please share intolerances (e.g. fish, shellfish, soy, gluten) before your visit.',
      'Vui lòng nêu dị ứng (cá, giáp xác, đậu nành, gluten) trước khi đến.',
    ),
    consent: loc3(
      'Ich stimme der Verarbeitung meiner Angaben für die Reservierung zu.',
      'I agree to process this data for reservation handling.',
      'Tôi đồng ý xử lý dữ liệu này cho đặt chỗ.',
    ),
    ctaLabel: loc3('Sicher senden', 'Send securely', 'Gửi bảo mật'),
  },
  media: {},
};

export const sushiHeroVideo: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiHeroVideo,
  componentType: LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere,
  registryBlockKey: REGISTRY_BLOCK.presentation.heroVideoAtmosphere,
  displayOrder: 8,
  content: {
    eyebrow: loc3('Theke · Abend', 'Counter · evening', 'Quầy · tối'),
    heading: loc3(
      'Reis, Messer, Rhythmus',
      'Rice, knife, rhythm',
      'Cơm, dao, nhịp',
    ),
    subheading: loc3(
      'Kurzer Blick hinter die Theke — ruhig, präzise, ohne Spektakel.',
      'A short look behind the counter — quiet, precise, without spectacle.',
      'Nhìn nhanh phía sau quầy — yên, chính xác, không phô trương.',
    ),
  },
  media: {
    heroVideo: 'https://cdn.coverr.co/videos/coverr-chef-preparing-food-1575/1080p.mp4',
    heroPoster: SUSHI_IMAGE,
  },
};

export const sushiFooter: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.sushiFooter,
  componentType: LAYOUT_COMPONENT_TYPE.footerStandard,
  displayOrder: 99,
  content: {
    siteTitle: loc3('Kiefer Theke', 'Kiefer Theke', 'Kiefer Theke'),
    address: loc3(
      'Amalienstraße 42, 80799 München',
      'Amalienstraße 42, 80799 Munich',
      'Amalienstraße 42, 80799 München',
    ),
    phone: loc3('+49 89 5550123', '+49 89 5550123', '+49 89 5550123'),
    email: loc3('theke@kiefer.example', 'theke@kiefer.example', 'theke@kiefer.example'),
    hours: loc3(
      'Di–Sa 12:00–14:30 · 17:30–22:00 · So/Mo geschlossen',
      'Tue–Sat 12:00–14:30 · 17:30–22:00 · Sun/Mon closed',
      'T3–T7 12:00–14:30 · 17:30–22:00 · CN/T2 nghỉ',
    ),
    legalLine: loc3(
      '© 2026 Kiefer Theke. Alle Rechte vorbehalten.',
      '© 2026 Kiefer Theke. All rights reserved.',
      '© 2026 Kiefer Theke. Bảo lưu mọi quyền.',
    ),
  },
  media: {},
};
